import { useEffect, useState } from "react";

import {
  Select,
  SelectData,
  TextField,
  Button,
  Typography,
} from "@king-ict/eupisi-ui/components";
import { useInput } from "@king-ict/eupisi-ui/hooks";
import { CodeBook, KindergartenRoleEnum } from "@king-ict/eupisi-ui/models";
import { nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { useTypedSelector } from "../../app";
import { useEnrollmentStatuses } from "../../hooks/enrollmentStatus/useEnrollmentStatuses";
import { EnrollmentFilters, InstitutionListItem } from "../../models";
import styles from "./EnrollmentSearchFilters.module.scss";

export interface EnrollmentSearchFiltersProps {
  handleSearch: any;
  setFilters: (value: React.SetStateAction<EnrollmentFilters>) => void;
  filters: EnrollmentFilters;
  handleClear: () => void;
  institutionsLocationsAndPrograms?: InstitutionListItem[];
  enrollmentStatusId?: number;
}

export const EnrollmentSearchFilters = ({
  handleSearch,
  setFilters,
  filters,
  enrollmentStatusId,
  handleClear,
  institutionsLocationsAndPrograms,
}: EnrollmentSearchFiltersProps) => {
  const user = useTypedSelector((state) => state.application.user);

  const { t } = useTranslation();

  const { handleChange } = useInput();

  const enrollmentStatuses = useEnrollmentStatuses();

  const [filteredInstitutions, setFilteredInstitutions] = useState<CodeBook[]>(
    [] as CodeBook[]
  );

  const [filteredLocations, setFilteredLocations] = useState<CodeBook[]>(
    [] as CodeBook[]
  );

  const [filteredPrograms, setFilteredPrograms] = useState<CodeBook[]>(
    [] as CodeBook[]
  );

  const handleKindergartensChange = (e: any) => {
    const { value } = e.target;

    setFilters((prev) => ({
      ...prev,
      kindergarten: value,
      location: undefined,
      program: undefined,
    }));

    let locations = [] as CodeBook[];
    const tempinstitutions = institutionsLocationsAndPrograms?.find(
      (x) => x.id === value
    );
    tempinstitutions?.locationsAndPrograms.forEach((locationAndProgram) => {
      if (
        locations.length === 0 ||
        locations.find((i) => i.id === locationAndProgram.locationId) == null
      ) {
        locations.push({
          id: locationAndProgram.locationId,
          name: locationAndProgram.locationName,
        });
      }
    });

    setFilteredLocations(locations);
    setFilteredPrograms([] as CodeBook[]);
  };

  useEffect(() => {
    if (user.institutionId && user.roleId !== KindergartenRoleEnum.GroupEnrollmentCommittee) {
      let locations = [] as CodeBook[];
      const tempinstitutions = institutionsLocationsAndPrograms?.find(
        (x) => x.id === user.institutionId
      );
      tempinstitutions?.locationsAndPrograms.forEach((locationAndProgram) => {
        if (
          locations.length === 0 ||
          locations.find((i) => i.id === locationAndProgram.locationId) == null
        ) {
          locations.push({
            id: locationAndProgram.locationId,
            name: locationAndProgram.locationName,
          });
        }
      });

      setFilteredLocations(locations);
    }
  }, [user.institutionId, institutionsLocationsAndPrograms]);

  useEffect(() => {
    if (user.enrollmentGroupId) {
      let institutions = [] as CodeBook[];
      institutionsLocationsAndPrograms?.forEach((institution) => {
        institutions.push({
          id: institution.id,
          name: institution.name,
        });
      });
      if (institutions.length !== 0) {
        setFilteredInstitutions(institutions);
      }
    }
    else {
      let institutions = [] as CodeBook[];
      let institution = institutionsLocationsAndPrograms?.find((i) => i.id === user.institutionId);  
      if (institution) {
        institutions.push({
          id: institution.id,
          name: institution.name,
        });
        setFilteredInstitutions(institutions);
      }
    }
  }, [user.enrollmentGroupId, institutionsLocationsAndPrograms]);

  const handleLocationsChange = (e: any) => {
    const { value } = e.target;

    setFilters((prev) => ({
      ...prev,
      location: value,
      program: undefined,
    }));
    let programs = [] as CodeBook[];

    const id =
    filters.kindergarten != null ? filters.kindergarten : user.institutionId;

    const tempinstitution = institutionsLocationsAndPrograms?.find(
      (x) => x.id === id
    );

    tempinstitution?.locationsAndPrograms.forEach((locationAndProgram) => {
      if (locationAndProgram.locationId === value) {
        if (
          programs.length === 0 ||
          programs.find((i) => i.id === locationAndProgram.programId) == null
        ) {
          programs.push({
            id: locationAndProgram.programId,
            name: locationAndProgram.locationProgramName,
          });
        }
      }
    });

    setFilteredPrograms(programs);
  };

  return (
    <>
      <Row className={styles.filterRow}>
        <Col md={6}>
          <TextField
            label={t("requestList.childOib")}
            name={nameof<typeof filters>("childOib")}
            value={filters.childOib}
            onChange={(e: any) => handleChange(e, setFilters)}
          />
        </Col>
        <Col md={6}>
          <TextField
            label={t("requestList.childLastName")}
            name={nameof<typeof filters>("childLastName")}
            value={filters.childLastName}
            onChange={(e: any) => handleChange(e, setFilters)}
          />
        </Col>
      </Row>
      <Row className={styles.filterRow}>
      {user.roleId === KindergartenRoleEnum.GroupEnrollmentCommittee && 
        <Col md={enrollmentStatusId === undefined ? 4 : 6}>
          <Select
            label={t("common.kindergaten")}
            name={nameof<typeof filters>("kindergarten")}
            value={filters.kindergarten}
            onChange={handleKindergartensChange}
            emptyOption={{
              value: undefined,
              text: "",
            }}
            selectData={
              {
                valueProp: "id",
                textProp: "name",
                data: filteredInstitutions,
              } as SelectData<CodeBook>
            }
          />
        </Col>
        } 

        <Col md={enrollmentStatusId === undefined ? 4 : 6}>
          <Select
            label={t("locationPrograms.location")}
            name={nameof<typeof filters>("location")}
            value={filters.location}
            onChange={handleLocationsChange}
            emptyOption={{
              value: undefined,
              text: "",
            }}
            selectData={
              {
                valueProp: "id",
                textProp: "name",
                data: filteredLocations,
              } as SelectData<CodeBook>
            }
          />
        </Col>
        <Col md={enrollmentStatusId === undefined ? 4 : 6}>
          <Select
            label={t("locationPrograms.program")}
            name={nameof<typeof filters>("program")}
            value={filters.program}
            onChange={(e: any) => handleChange(e, setFilters)}
            emptyOption={{
              value: undefined,
              text: "",
            }}
            selectData={
              {
                valueProp: "id",
                textProp: "name",
                data: filteredPrograms,
              } as SelectData<CodeBook>
            }
          />
        </Col>
        {enrollmentStatusId === undefined && (
          <Col md={4}>
            <Select
              label={t("common.status")}
              name={nameof<typeof filters>("enrollmentStatusId")}
              value={filters.enrollmentStatusId}
              onChange={(e: any) => handleChange(e, setFilters)}
              emptyOption={{ value: undefined, text: "" }}
              selectData={
                {
                  valueProp: "id",
                  textProp: "name",
                  data: enrollmentStatuses,
                } as SelectData<CodeBook>
              }
            />
          </Col>
        )}
      </Row>
      <Row>
        <Col md={12}>
          <div className={styles.searchButtons}>
            <Button color="primary" onClick={handleSearch}>
              {t("common.search")}
            </Button>
            <Typography color="primary" onClick={handleClear}>
              {t("common.clearFilters")}
            </Typography>
          </div>
        </Col>
      </Row>
    </>
  );
};
